import { call, delay, put, select, takeLatest } from 'redux-saga/effects'
import {
  AdminFetchOnboardingTemplate,
  AdminFetchTemplatePreview,
  AdminSaveOnboardingTemplate,
  FlowEditorApplyOp,
  FlowEditorSetDraft,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type { CollectDataTemplatePreviewResponse } from 'src/interfaces/collectDataTemplatePreview'
import type { CollectDataTemplateUpdateDto } from 'src/interfaces/collectDataTemplateUpdate'
import type { OnboardingTemplate } from 'src/interfaces/onboardingTemplate'
import { getFlowEditorDraft, getFlowEditorSavedSnapshot } from 'src/selectors/onboardingFlowEditor'
import { buildActionsFromDocumentDiff } from 'src/utils/flowEditorDocument/buildActionsFromDiff'
import { templateToDocument } from 'src/utils/flowEditorDocument/fromApi'
import type { FlowEditorTemplateDocument } from 'src/utils/flowEditorDocument/types'
import { captureSentryError } from 'src/utils/sentry'
import { callSecureApi } from './api/callApi'

/** Debounce window for the live preview after the draft changes. */
const PREVIEW_DEBOUNCE_MS = 500

/**
 * Recompute the live preview whenever the draft changes. The draft is mutated
 * by `FlowEditorApplyOp` (edits), `FlowEditorSetDraft` (revert), and the
 * fetch/save success cases (reset to baseline) — so the saga watches all of
 * them. `takeLatest` + an opening `delay` debounces a burst of edits and
 * cancels any in-flight request when a newer change arrives.
 */
export function* onDraftChanged() {
  yield delay(PREVIEW_DEBOUNCE_MS)

  const savedSnapshot: OnboardingTemplate | null = yield select(getFlowEditorSavedSnapshot)
  const draft: FlowEditorTemplateDocument | null = yield select(getFlowEditorDraft)

  if (!savedSnapshot || !draft) {
    return
  }

  // New-template mode: the draft carries no template id until the create request comes back. The
  // preview endpoint diffs pending changes against a *persisted* template, so there is nothing to
  // preview yet — and interpolating the empty id would request `…/collect-data-templates//preview`.
  // Clear the panel instead of calling the API.
  if (draft.templateId === '') {
    yield put(AdminFetchTemplatePreview.success({ configs: [] }))

    return
  }

  // Same diff the save flow sends — baseline snapshot vs current draft.
  const baselineDoc = templateToDocument(savedSnapshot)
  const actions = buildActionsFromDocumentDiff(baselineDoc, draft)

  // No pending changes → clear the panel without hitting the API.
  if (actions.length === 0) {
    yield put(AdminFetchTemplatePreview.success({ configs: [] }))

    return
  }

  yield put(AdminFetchTemplatePreview.request())

  try {
    const endpoint = Endpoints.AdminPreviewOnboardingTemplate.replace(
      ':collectDataTemplateId',
      draft.templateId,
    )
    const body: CollectDataTemplateUpdateDto = { actions }

    const { payload }: { payload: CollectDataTemplatePreviewResponse } = yield call(
      callSecureApi,
      endpoint,
      {
        method: 'POST',
        body,
        // Backend `BooleanQueryTransform` parses the string flag into a boolean.
        query: { includeCounts: 'true' },
      },
    )

    yield put(AdminFetchTemplatePreview.success(payload))
  } catch (err: unknown) {
    // Only genuine endpoint failures reach here (takeLatest cancels superseded
    // edits before this point). Report to Sentry, but stay quiet in the UI —
    // this runs on every edit, so a toast would be noise; the panel already
    // surfaces the error state from `.failed`.
    captureSentryError(err, {
      message: '[saga.templatePreview.onDraftChanged] error',
      data: { collectDataTemplateId: draft.templateId },
    })
    yield put(AdminFetchTemplatePreview.failed(err))
  }
}

export function* templatePreviewSaga() {
  yield takeLatest(
    [
      FlowEditorApplyOp.type,
      FlowEditorSetDraft.type,
      AdminFetchOnboardingTemplate.type.SUCCESS,
      AdminSaveOnboardingTemplate.type.SUCCESS,
    ],
    onDraftChanged,
  )
}
